import { IconButton, Tooltip, Icon, colors } from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { add } from 'ezmoney';
import { type ReactNode } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import {
  getPercentageClassName,
  Percentage,
} from 'src/core/modules/budgets/components/Percentage';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import { BudgetGauge } from './BudgetGauge';
import {
  type BudgetBreakdown,
  hasExceededAmounts,
  getSpentBreakdownRatio,
  getUsedAmount,
  getBudgetLimit,
  getCommittedAmount,
  getAvailableAmount,
  getExceededAmount,
  getExceededBreakdownRatio,
  getAvailableBreakdownRatio,
} from '../../../../models/breakdown';

export const BudgetRow = ({
  rowName,
  breakdown,
  collapse,
  rowIcon,
  onRowClick,
  isArchived,
  className,
  depth = 0,
}: {
  rowName: string;
  breakdown: BudgetBreakdown;
  collapse?: { isCollapsed: boolean; onToogleCollapse: () => void };
  rowIcon?: ReactNode;
  onRowClick?: () => void;
  isArchived?: boolean;
  className?: string;
  depth?: number;
}) => {
  const isExceeded = hasExceededAmounts(breakdown);
  const { t } = useTranslation('global');

  return (
    <div className="flex items-center">
      {collapse ? (
        <IconButton
          iconName="chevron-down"
          className={classNames(collapse.isCollapsed && '-rotate-90', 'ml-8')}
          color={colors.contentPrimary}
          aria-label={rowName}
          variant="tertiaryNeutral"
          onClick={collapse.onToogleCollapse}
        />
      ) : (
        <div className="w-48" /> // 48px is the width of the icon to preserve the layout
      )}
      <div
        role="button"
        aria-label={rowName}
        tabIndex={0}
        className={classNames(
          'rounded-8 grid w-[100%] grid-cols-4',
          onRowClick && 'hover:bg-background-primary-hover cursor-pointer',
          className,
        )}
        onClick={onRowClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            onRowClick?.();
          }
        }}
      >
        {/* Budget name */}
        <div>
          <div
            className="text-content-primary title-m flex items-center gap-8 pl-8"
            style={{ marginLeft: depth > 0 ? `${(depth - 1) * 12}px` : '0px' }}
          >
            {rowIcon && <div className="flex items-center">{rowIcon}</div>}
            <div className="flex flex-col">
              <div className="flex items-center gap-8">
                {rowName}
                {isArchived && (
                  <Tooltip
                    content={t('budget.overviewPage.expenseCategoryDeleted')}
                    placement="top"
                  >
                    <Icon
                      className="mt-[5px]"
                      size="m"
                      color={colors.contentDecorativeIcon}
                      name="circle-information"
                    />
                  </Tooltip>
                )}
              </div>
              <div
                className={classNames(
                  'body-m',
                  getPercentageClassName({ over: isExceeded }),
                )}
              >
                {!isArchived && (
                  <Percentage ratio={getSpentBreakdownRatio(breakdown)} />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Gauges */}
        <BudgetGauge
          isExceeded={isExceeded}
          spent={add(getUsedAmount(breakdown), getCommittedAmount(breakdown))}
          budget={getBudgetLimit(breakdown)}
        />

        {/* Committed */}
        <div className="text-content-primary title-m flex flex-col items-end justify-center">
          {formatMonetaryValue(getCommittedAmount(breakdown))}
        </div>

        {/* Available */}
        <div className="text-right">
          {!isArchived && (
            <>
              <div
                className={classNames(
                  'title-m',
                  isExceeded
                    ? 'text-content-alert-default'
                    : 'text-content-primary',
                )}
              >
                {formatMonetaryValue(
                  isExceeded
                    ? getExceededAmount(breakdown)
                    : getAvailableAmount(breakdown),
                )}
              </div>
              <div
                className={classNames(
                  'body-m',
                  getPercentageClassName({ over: isExceeded }),
                )}
              >
                {isExceeded ? (
                  <>
                    +<Percentage ratio={getExceededBreakdownRatio(breakdown)} />
                  </>
                ) : (
                  <Percentage ratio={getAvailableBreakdownRatio(breakdown)} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
